import { Button } from '@plato/ui-legacy/components/ui/button';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useDateFnsLocale } from '@plato/ui-legacy/lib/hooks/use-date-fns-locale';
import { DatePicker } from '@plato/ui/components/date-picker';
import { addWeeks, intlFormat } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export type DeliveryDateFieldProps = {
    /** The field's own label, which names the calendar and heads its sheet. */
    label: string;
    onChange: (value: string) => void;
    readonly?: boolean;
    value: string;
};

export function DeliveryDateInput({
    label,
    onChange,
    readonly,
    value,
}: DeliveryDateFieldProps): ReactElement {
    const { t } = useTranslation('quotes-orders-header');
    const locale = useResolvedLocale();
    const dateFnsLocalization = useDateFnsLocale(locale);
    const [showDateModal, setShowDateModal] = useState(false);

    return (
        <div className="flex flex-row gap-2 flex-wrap">
            <DatePicker
                title={label}
                placeholder={label}
                locale={dateFnsLocalization}
                open={showDateModal}
                onOpenChange={setShowDateModal}
                value={value ? new Date(value) : undefined}
                onValueChange={(date) => {
                    if (date) {
                        onChange(date.toISOString());
                    }
                }}
                render={
                    <Button className="grow max-sm:w-full" disabled={readonly} variant="outline">
                        {value ? (
                            intlFormat(new Date(value), { dateStyle: 'long' }, { locale })
                        ) : (
                            <span className="mr-auto text-sm"></span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 text-neutral-300" />
                    </Button>
                }
            />
            <Button
                disabled={readonly}
                variant="outline"
                type="button"
                onClick={() => {
                    onChange(addWeeks(new Date(), 1).toISOString());
                }}
            >
                {t('button__in_1_week')}
            </Button>
            <Button
                disabled={readonly}
                variant="outline"
                type="button"
                onClick={() => {
                    onChange(addWeeks(new Date(), 4).toISOString());
                }}
            >
                {t('button__in_4_week')}
            </Button>
        </div>
    );
}
